/* eslint-disable parog-web/no-glint-expect-error-directive */
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import dayjs from 'dayjs';
import {
  validateDate,
  validateNumber
} from 'ember-changeset-validations/validators';
import BaseMedicalHistoryFormComponent from 'parog-web/components/medical-history/abstract/base-edition-form';
import CardioTransplantPathology from 'parog-web/components/medical-history/cardio/transplant/pathology-fields';
import MedicalHistoryBaseEditionForm from './base-edition-form';
import CardioGenericDiagnostic from './cardio/cardio-generic/diagnostic-fields';
import CardioHeartFailureDiagnostic from './cardio/heart-failure/diagnostic-fields';
import CardioHeartFailurePathology from './cardio/heart-failure/pathology-fields';
import CardioTransplantDiagnostic from './cardio/transplant/diagnostic-fields';

const DiagnosticFields = {
  'cardio-generic': CardioGenericDiagnostic,
  'heart-failure': CardioHeartFailureDiagnostic,
  transplant: CardioTransplantDiagnostic
};

const PathologyFields = {
  'heart-failure': CardioHeartFailurePathology,
  transplant: CardioTransplantPathology
};

export default class MedicalHistoryFormComponent extends BaseMedicalHistoryFormComponent {
  override journalEventProperties = [
    ...this.sharedProperties,
    ...this.cardioPathologyProperties
  ];

  get cardioPathologyProperties(): string[] {
    return [
      'transplantDate',
      'transplantRank',
      'transplantedOrgan',
      'underlyingDisease',
      'cytomegalovirusStatus',
      'heartFailureType',
      'diagnosticDate',
      'nyhaStage',
      'fevg'
    ];
  }

  get diagnostic(): TemplateOnlyComponent {
    return DiagnosticFields[this.patho ?? ''];
  }

  get pathology(): TemplateOnlyComponent {
    return PathologyFields[this.patho ?? ''];
  }

  override get validations(): Record<string, unknown> {
    return {
      fevg: validateNumber({
        allowBlank: true,
        positive: true,
        gte: 0,
        lte: 100,
        integer: true,
        description: 'FEVG'
      }),
      transplantDate: [
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          onOrAfter: new Date('01/01/1900')
        }),
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
          onOrBefore: dayjs().endOf('day').toDate()
        })
      ],
      diagnosticDate: [
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          onOrAfter: new Date('01/01/1900')
        }),
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
          onOrBefore: dayjs().endOf('day').toDate()
        })
      ]
    };
  }

  override retrieveModel(): Record<string, unknown> {
    const model = super.retrieveModel();
    if (!this.isLoadedFromJe) {
      model.transplantedOrgan = 'heart';
    }

    return model;
  }

  <template>
    <MedicalHistoryBaseEditionForm
      {{! @glint-expect-error }}
      @forcComponent={{this}}
      @patient={{@patient}}
      as |mh|
    >
      <mh.FurSection />
      <mh.FurDetailsSection @diagnostic={{this.diagnostic}} />
      <mh.ComorbiditiesAllergiesSection />
      <mh.PathologySection @pathology={{this.pathology}} />
      <mh.VaccinesSection />
      <mh.CommentsSection />
    </MedicalHistoryBaseEditionForm>
  </template>
}
